import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/styles";
import { useSelector } from "react-redux";
import { 
    Card, 
    CardContent, 
    Typography, 
    Grid, 
    Chip, 
    IconButton,
    Collapse,
    Box,
    Link,
    Button,
    ExpansionPanel,
    ExpansionPanelSummary,
    ExpansionPanelDetails
} from "@material-ui/core";
import { formatDateTime, isStaffUser, canApproveTimesheet } from "utils";
import { formatTimesheetApprovalStatus } from "../format-timesheet-approval-status";
import { CrudOptions, Lookup } from "frame/components";
import { getGoogleMapsLinkByLocationQuery } from "utils";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import AccessAlarmIcon from "@material-ui/icons/AccessAlarm";
import ErrorIcon from "@material-ui/icons/Error";
import RefreshIcon from "@material-ui/icons/Refresh";
import CancelIcon from "@material-ui/icons/Cancel";
import StopIcon from "@material-ui/icons/Stop";
import CloudOffIcon from "@material-ui/icons/CloudOff";
import HistoryIcon from "@material-ui/icons/History";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
	root: {
		marginBottom: theme.spacing(1),
		borderRadius: theme.spacing(1),
		border: `0px solid rgba(0, 0, 0, 0.08)`,
		backgroundColor: theme.palette.background.paper,
		transition: "all 0.2s ease-in-out",
		position: "relative",
		"&:hover": {
			boxShadow: theme.shadows[1]
		}
	},
	content: {
		padding: theme.spacing(0.7, 1.25, 0.5, 1.25),
		"&:last-child": {
			paddingBottom: theme.spacing(0.5)
		}
	},
	collapsedContent: {
		display: "block",
		minHeight: "60px",
		// Slightly reduce vertical padding further
		paddingTop: theme.spacing(0.5),
		paddingBottom: theme.spacing(0.5),
		// Match horizontal padding with grid cells so text aligns
		paddingLeft: theme.spacing(0.5),
		paddingRight: theme.spacing(0.5)
	},
	collapsedRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: theme.spacing(0.5),
		"&:last-child": {
			// Remove extra bottom gap on the final row to balance with the top
			marginBottom: 0
		}
	},
	collapsedLeft: {
		flex: 1,
		minWidth: 0
	},
	collapsedRight: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		flexShrink: 0
	},
	expandedContent: {
		paddingTop: theme.spacing(0.5)
	},
	detailsTable: {
		display: "grid",
		gridTemplateColumns: "1fr 1fr",
		gap: theme.spacing(0.5)
	},
	row: {
		display: "contents"
	},
	cell: {
		padding: theme.spacing(0.5)
	},
	singleCell: {
		gridColumn: "1 / -1",
		// Match padding with regular cells to keep left alignment consistent
		padding: theme.spacing(0.5)
	},
	siteName: {
		fontWeight: 600,
		fontSize: "1rem",
		color: theme.palette.text.primary
	},
	staffName: {
		fontSize: "0.875rem",
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(0.5)
	},
	timeInfo: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
		fontSize: "0.875rem",
		fontWeight: 500
	},
		timesheetIcon: {
			color: theme.palette.success.main,
			fontSize: "1rem",
			// Keep a little right inset so the clock visually aligns with the expand chevron below.
			marginRight: 4
		},
	activeTimerText: {
		fontVariantNumeric: "tabular-nums",
		letterSpacing: 0.2,
	},
	statusChip: {
		height: "24px",
		fontSize: "0.75rem"
	},
	active: {
		backgroundColor: theme.palette.success.main,
		color: theme.palette.white
	},
	closed: {
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.white
	},
	pending: {
		backgroundColor: theme.palette.warning.main,
		color: theme.palette.warning.contrastText
	},
	detailValueRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
		flexWrap: "wrap",
	},
	offlineCapturedIcon: {
		fontSize: "1rem",
		color: theme.palette.text.secondary,
		display: "inline-flex",
		alignItems: "center",
	},
	timesEditedIcon: {
		fontSize: "1rem",
		color: theme.palette.warning.dark || theme.palette.warning.main,
		display: "inline-flex",
		alignItems: "center",
	},
	changeLog: {
		marginTop: theme.spacing(0.5),
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(0.75),
	},
	changeLogEntry: {
		fontSize: "0.8125rem",
		color: theme.palette.text.secondary,
		lineHeight: 1.35,
	},
	changeLogMeta: {
		fontSize: "0.75rem",
		color: theme.palette.text.tertiary,
	},
	editsAccordion: {
		borderRadius: theme.spacing(0.5),
		border: `1px solid ${theme.palette.divider}`,
		backgroundColor: theme.palette.background.default,
		boxShadow: "none",
		marginBottom: theme.spacing(1.5),
		"&:before": {
			display: "none",
		},
		"&.Mui-expanded": {
			marginBottom: theme.spacing(1.5),
		},
	},
	editsAccordionSummary: {
		padding: theme.spacing(0, 1),
		minHeight: 40,
		"&.Mui-expanded": {
			minHeight: 40,
		},
		"& .MuiExpansionPanelSummary-content": {
			margin: theme.spacing(0.75, 0),
		},
	},
	editsAccordionTitle: {
		fontSize: "0.8125rem",
		fontWeight: 600,
		color: theme.palette.text.secondary,
	},
	editsAccordionDetails: {
		padding: theme.spacing(0, 1.5, 1),
		display: "block",
	},
	failed: {
		backgroundColor: theme.palette.error.main,
		color: theme.palette.error.contrastText
	},
	overlap: {
		backgroundColor: theme.palette.error.main,
		color: theme.palette.error.contrastText,
		"& .MuiChip-icon": {
			color: theme.palette.error.contrastText
		}
	},
	pending: {
		backgroundColor: theme.palette.grey[400],
		color: theme.palette.getContrastText(theme.palette.grey[400]),
	},
	syncActions: {
		display: "flex",
		gap: theme.spacing(0.5),
		marginTop: theme.spacing(0.5)
	},
	syncButton: {
		minWidth: "auto",
		padding: theme.spacing(0.5, 1),
		fontSize: "0.75rem"
	},
	statusIcon: {
		fontSize: "16px",
		marginRight: theme.spacing(0.5)
	},
	activeIcon: {
		color: theme.palette.success.main
	},
	closedIcon: {
		color: theme.palette.error.main
	},
	expandButton: {
		padding: 0
	},
	detailsGrid: {
		marginTop: theme.spacing(0.5)
	},
	detailItem: {
		marginBottom: theme.spacing(1)
	},
	detailLabel: {
		fontSize: "0.75rem",
		color: theme.palette.text.tertiary,
		fontWeight: 500,
		marginBottom: theme.spacing(0.25),
		lineHeight: 1.2
	},
	detailValue: {
		fontSize: "1rem",
		color: theme.palette.text.primary,
		fontWeight: 500
	},
	notesValue: {
		whiteSpace: "pre-line",
		wordBreak: "break-word",
	},
	mapLink: {
		marginTop: 0
	},
	actionsContainer: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1)
	},
	// Text truncation styles
	truncatedStaffName: {
		textOverflow: "ellipsis",
		overflow: "hidden",
		whiteSpace: "nowrap",
		width: "100%"
	},
	truncatedSiteName: {
		textOverflow: "ellipsis",
		overflow: "hidden",
		whiteSpace: "nowrap",
		lineHeight: "1.2em",
		width: "100%"
	}
}));

export const TimesheetCardCollapsed = ({ timesheet, onDelete, onEdit, onClone, role, isLeaveItem = false, forceExpanded = false, hideActions = false, onRetrySync, onDiscardSync, onEndTimesheet, onApprove, onUnapprove, supervisingStaffList = [] }) => {
	const classes = useStyles();
	const [expanded, setExpanded] = useState(false);
	const [editsExpanded, setEditsExpanded] = useState(false);
	const [activeElapsed, setActiveElapsed] = useState("00:00:00");
	const featureFlags = useSelector(({ featureFlags }) => featureFlags);
	const currentStaffId = useSelector(({ profile }) => profile?.staffId);
	const equipmentEnabled = featureFlags?.equipment === true; // Default to false if not loaded
	const manualTimesheetEntriesEnabled = featureFlags?.manualTimesheetEntries === true; // Default to false if not loaded
	const timeEditHistoryEnabled = featureFlags?.timesheetTimeEditHistory === true;
	const timesheetApprovalsEnabled = featureFlags?.timesheetApprovals === true;
	const hasTimeEdits = timeEditHistoryEnabled && (
		timesheet.hasTimeEdits === true ||
		(Array.isArray(timesheet.timeFieldChanges) && timesheet.timeFieldChanges.length > 0)
	);
	const timeFieldChanges = hasTimeEdits && Array.isArray(timesheet.timeFieldChanges)
		? timesheet.timeFieldChanges
		: [];
	const editedFields = new Set(timeFieldChanges.map((change) => change.fieldName));
	const startEdited = editedFields.has("Start");
	const endEdited = editedFields.has("End");
	const lunchEdited = editedFields.has("Lunch");
	const showOfflineIcon =
		timesheet.syncedFromOffline && !timesheet.pendingSync && !timesheet.syncFailed;

	// Determine if staff can edit this timesheet
	// Staff can edit if: Manual Timesheet Entries is enabled AND user is staff AND timesheet belongs to current staff
	const canStaffEdit = manualTimesheetEntriesEnabled && 
		isStaffUser(role) && 
		currentStaffId && 
		timesheet.staffId && 
		timesheet.staffId.toString() === currentStaffId.toString();
	
	// Determine if staff can clone this timesheet
	// Staff can clone if: manualTimesheetEntries flag is enabled AND user is staff AND timesheet belongs to current staff
	const canStaffClone = manualTimesheetEntriesEnabled && 
		isStaffUser(role) && 
		currentStaffId && 
		timesheet.staffId && 
		timesheet.staffId.toString() === currentStaffId.toString();

	const showApprovalChip =
		timesheetApprovalsEnabled &&
		!timesheet.isActive &&
		!isLeaveItem &&
		!timesheet.pendingSync &&
		!timesheet.syncFailed &&
		!timesheet.isApproved;

	const showApprovalStatus =
		timesheetApprovalsEnabled &&
		!timesheet.isActive &&
		!isLeaveItem &&
		!timesheet.pendingSync &&
		!timesheet.syncFailed;

	const userCanApprove =
		timesheetApprovalsEnabled &&
		canApproveTimesheet({
			role,
			supervisingStaffList,
			currentStaffId,
			timesheetStaffId: timesheet.staffId,
		});

	const showApprovalActions =
		userCanApprove &&
		!timesheet.isActive &&
		!isLeaveItem &&
		!timesheet.pendingSync &&
		!timesheet.syncFailed &&
		!hideActions;

	const formatChangeValue = (fieldName, value) => {
		if (value == null || value === "") return "—";
		if (fieldName === "Lunch") {
			const mins = Number(value);
			return Number.isFinite(mins) ? `${mins} min` : String(value);
		}
		const parsed = moment(value);
		return parsed.isValid() ? formatDateTime(value) : String(value);
	};

	const fieldLabel = (fieldName) => {
		if (fieldName === "Lunch") return "Breaks";
		return fieldName;
	};

	useEffect(() => {
		setExpanded(!!forceExpanded);
	}, [forceExpanded]);

	// Running timer display for active timesheets in the list (based on server UTC start time).
	useEffect(() => {
		if (!timesheet?.isActive || !timesheet?.startDateTimeUTC) {
			setActiveElapsed("00:00:00");
			return;
		}

		const updateElapsed = () => {
			const start = moment.utc(timesheet.startDateTimeUTC);
			if (!start.isValid()) {
				setActiveElapsed("00:00:00");
				return;
			}

			const now = moment.utc();
			const duration = moment.duration(Math.max(0, now.diff(start)));
			const hours = Math.floor(duration.asHours());
			const minutes = duration.minutes();
			const seconds = duration.seconds();

			setActiveElapsed(
				String(hours).padStart(2, "0") +
				":" +
				String(minutes).padStart(2, "0") +
				":" +
				String(seconds).padStart(2, "0")
			);
		};

		updateElapsed();
		const interval = setInterval(updateElapsed, 1000);
		return () => clearInterval(interval);
	}, [timesheet?.isActive, timesheet?.startDateTimeUTC]);

	const toggleExpanded = () => {
		setExpanded(!expanded);
	};


	const startLocationGoogleMapLink =
		timesheet.startLatitude && timesheet.startLongitude
			? getGoogleMapsLinkByLocationQuery(timesheet.startLatitude, timesheet.startLongitude)
			: null;

	const endLocationGoogleMapLink =
		timesheet.endLatitude && timesheet.endLongitude
			? getGoogleMapsLinkByLocationQuery(timesheet.endLatitude, timesheet.endLongitude)
			: null;

	// Calculate duration for closed timesheets only
	const getDuration = () => {
		if (!timesheet.startDateTimeUTC) return "-";
		if (timesheet.isActive) return ""; // No live timer for active entries
		
		const start = moment.utc(timesheet.startDateTimeUTC);
		if (!timesheet.endDateTimeUTC) return "In Progress";
		const end = moment.utc(timesheet.endDateTimeUTC);
		
		const duration = moment.duration(end.diff(start));
		const hours = Math.floor(duration.asHours());
		const minutes = duration.minutes();
		
		if (hours > 0) {
			return `${hours}h ${minutes}m`;
		}
		return `${minutes}m`;
	};

	// Helper: format a duration (in minutes) as "xh ym"
	const formatMinutes = (totalMinutes) => {
		if (totalMinutes == null || isNaN(totalMinutes)) { return "-"; }
		const hours = Math.floor(totalMinutes / 60);
		const minutes = Math.round(totalMinutes % 60);
		if (hours > 0) { return `${hours}h ${minutes}m`; }
		return `${minutes}m`;
	};

	// Compute gross and net minutes for closed timesheets (show "-" if in progress)
	const computeGrossMinutes = () => {
		if (!timesheet.startDateTimeUTC || !timesheet.endDateTimeUTC) { return null; }
		const start = moment.utc(timesheet.startDateTimeUTC);
		const end = moment.utc(timesheet.endDateTimeUTC);
		return Math.max(0, Math.round(moment.duration(end.diff(start)).asMinutes()));
	};

	const computeNetMinutes = () => {
		const gross = computeGrossMinutes();
		if (gross == null) { return null; }
		const lunch = parseInt(timesheet.lunchTimeMinutes || 0, 10);
		return Math.max(0, gross - lunch);
	};

	return (
		<Card className={classes.root}>
			<CardContent className={classes.content}>
				{/* Collapsed View */}
				<div className={classes.collapsedContent}>
					{/* Row 1: Staff name | Duration */}
					<div className={classes.collapsedRow}>
						<div className={classes.collapsedLeft}>
							<Typography className={`${classes.siteName} ${classes.truncatedStaffName}`}>
								{timesheet.staffFullName || <Lookup id={timesheet.staffId} resource="staff" attribute="fullName" />}
							</Typography>
						</div>
						<div className={classes.collapsedRight}>
							<div className={classes.timeInfo}>
								{timesheet.isActive ? (
									<>
										<span className={classes.activeTimerText}>{activeElapsed}</span>
										<AccessAlarmIcon className={classes.timesheetIcon} />
									</>
								) : (
									<>{getDuration()}</>
								)}
							</div>
						</div>
					</div>

					{/* Row 2: Site name | Status + Expand */}
					<div className={classes.collapsedRow}>
						<div className={classes.collapsedLeft}>
							<Typography className={`${classes.staffName} ${classes.truncatedSiteName}`}>
								<Lookup id={timesheet.siteId} resource="sites" attribute="siteName" />
							</Typography>
						</div>
						<div className={classes.collapsedRight}>
							{timesheet.syncFailed && (
								<Chip
									size="small"
									icon={<ErrorIcon />}
									label={`Sync failed (${timesheet.failedCount || 0})`}
									className={`${classes.statusChip} ${classes.failed}`}
								/>
							)}
							{timesheet.pendingSync && !timesheet.syncFailed && (
								<Chip
									size="small"
									label="Pending sync"
									className={`${classes.statusChip} ${classes.pending}`}
								/>
							)}
							{showApprovalChip && (
								<Chip
									size="small"
									label="Pending"
									className={`${classes.statusChip} ${classes.pending}`}
								/>
							)}
							{!isLeaveItem && timesheet.hasOverlap && (
								<Chip
									size="small"
									icon={<ErrorIcon />}
									label="Overlaps"
									title="This overlaps with another timesheet"
									className={`${classes.statusChip} ${classes.overlap}`}
								/>
							)}
							<IconButton 
								className={classes.expandButton}
								onClick={toggleExpanded}
								size="small"
								aria-label={expanded ? "Collapse" : "Expand"}
							>
								{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
							</IconButton>
						</div>
					</div>
				</div>

				{/* Expanded View */}
				<Collapse in={expanded}>
						<div className={classes.expandedContent}>
							<div className={classes.detailsTable}>
								{/* Row: Start time/day | Start geo */}
								<div className={classes.row}>
									<div className={classes.cell}>
										<Typography className={classes.detailLabel}>Start</Typography>
										<Typography className={`${classes.detailValue} ${classes.detailValueRow}`}>
											{formatDateTime(timesheet.startDateTimeUTC)}
											{showOfflineIcon && (
												<span className={classes.offlineCapturedIcon} aria-label="Offline captured">
													<CloudOffIcon fontSize="inherit" />
												</span>
											)}
											{startEdited && (
												<span className={classes.timesEditedIcon} aria-label="Start time edited">
													<HistoryIcon fontSize="inherit" />
												</span>
											)}
										</Typography>
									</div>
									<div className={classes.cell}>
										<Typography className={classes.detailLabel}>&nbsp;</Typography>
										{!isLeaveItem && (
											<Typography className={`${classes.detailValue} ${classes.mapLink}`}>
												{startLocationGoogleMapLink ? (
													<Link
														href={startLocationGoogleMapLink}
														color="secondary"
														variant="inherit"
														target="_blank"
														rel="noopener noreferrer"
													>
														Map
													</Link>
												) : (
													"-"
												)}
											</Typography>
										)}
									</div>
								</div>

								{/* Row: End time/day | End geo */}
								<div className={classes.row}>
									<div className={classes.cell}>
										<Typography className={classes.detailLabel}>End</Typography>
										<Typography className={`${classes.detailValue} ${classes.detailValueRow}`}>
											{timesheet.endDateTimeUTC ? formatDateTime(timesheet.endDateTimeUTC) : "-"}
											{showOfflineIcon && timesheet.endDateTimeUTC && (
												<span className={classes.offlineCapturedIcon} aria-label="Offline captured">
													<CloudOffIcon fontSize="inherit" />
												</span>
											)}
											{endEdited && (
												<span className={classes.timesEditedIcon} aria-label="End time edited">
													<HistoryIcon fontSize="inherit" />
												</span>
											)}
										</Typography>
									</div>
									<div className={classes.cell}>
										<Typography className={classes.detailLabel}>&nbsp;</Typography>
										{!isLeaveItem && (
											<Typography className={`${classes.detailValue} ${classes.mapLink}`}>
												{endLocationGoogleMapLink ? (
													<Link
														href={endLocationGoogleMapLink}
														color="secondary"
														variant="inherit"
														target="_blank"
														rel="noopener noreferrer"
													>
														Map
													</Link>
												) : (
													"-"
												)}
											</Typography>
										)}
									</div>
								</div>

								{/* Breaks (single row spanning both columns) */}
								{!isLeaveItem && (
									<div className={classes.singleCell}>
										<Typography className={classes.detailLabel}>Breaks</Typography>
										<Typography className={`${classes.detailValue} ${classes.detailValueRow}`}>
											{timesheet.lunchTimeMinutes ? `${timesheet.lunchTimeMinutes} minutes` : "None"}
											{lunchEdited && (
												<span className={classes.timesEditedIcon} aria-label="Breaks edited">
													<HistoryIcon fontSize="inherit" />
												</span>
											)}
										</Typography>
									</div>
								)}

								{!isLeaveItem && timesheet.hasOverlap && (
									<div className={classes.singleCell}>
										<Typography className={classes.detailLabel}>Overlap</Typography>
										<Typography className={classes.detailValue}>
											This overlaps with another timesheet
										</Typography>
									</div>
								)}

								{/* Total and Net time on same row */}
								<div className={classes.row}>
									<div className={classes.cell}>
										<Typography className={classes.detailLabel}>Total time</Typography>
										<Typography className={classes.detailValue}>
											{formatMinutes(computeGrossMinutes())}
										</Typography>
									</div>
									<div className={classes.cell}>
										<Typography className={classes.detailLabel}>Net time</Typography>
										<Typography className={classes.detailValue}>
											{formatMinutes(computeNetMinutes())}
										</Typography>
									</div>
								</div>

								{/* Work type and Equipment on same row */}
								{!isLeaveItem && (
									<div className={classes.row}>
										<div className={classes.cell}>
											<Typography className={classes.detailLabel}>Work type</Typography>
											<Typography className={classes.detailValue}>
												<Lookup id={timesheet.organisationTaskTypeId} resource="taskTypes" attribute="taskType" />
											</Typography>
										</div>
										{equipmentEnabled && (
											<div className={classes.cell}>
											<Typography className={classes.detailLabel}>Equipment</Typography>
											<Typography className={classes.detailValue}>
												{timesheet.equipment && timesheet.equipment.length > 0
													? timesheet.equipment.map((e) => e.equipmentName || "").filter(Boolean).join(", ")
													: "—"}
											</Typography>
											</div>
										)}
									</div>
								)}

								{/* Staff type and Phone on one line */}
								<div className={classes.row}>
									<div className={classes.cell}>
										<Typography className={classes.detailLabel}>Staff type</Typography>
										<Typography className={classes.detailValue}>
											{timesheet.organisationStaffType || "-"}
										</Typography>
									</div>
									<div className={classes.cell}>
										<Typography className={classes.detailLabel}>Phone</Typography>
										<Typography className={classes.detailValue}>
											<Lookup id={timesheet.staffId} resource="staff" attribute="phoneNumber" />
										</Typography>
									</div>
								</div>

								<div className={classes.singleCell}>
									<Typography className={classes.detailLabel}>Notes</Typography>
									<Typography className={`${classes.detailValue} ${classes.notesValue}`}>
										{timesheet.notes || "-"}
									</Typography>
								</div>

								{showApprovalStatus && (
									<div className={classes.singleCell}>
										<Typography className={classes.detailLabel}>Status</Typography>
										<Typography className={classes.detailValue}>
											{formatTimesheetApprovalStatus(timesheet)}
										</Typography>
									</div>
								)}

								{/* Sync failure actions */}
								{(timesheet.syncFailed || timesheet.pendingSync) && (onRetrySync || onDiscardSync) && (
									<div className={classes.singleCell}>
										<Typography className={classes.detailLabel}>
											{timesheet.syncFailed ? "Sync Status" : "Sync Status"}
										</Typography>
										<div className={classes.syncActions}>
											{(timesheet.syncFailed || (timesheet.pendingSync && timesheet.lastError)) && onRetrySync && (
												<Button
													variant="outlined"
													size="small"
													startIcon={<RefreshIcon />}
													onClick={onRetrySync}
													className={classes.syncButton}
													color="primary"
												>
													Retry
												</Button>
											)}
											{(timesheet.syncFailed || timesheet.pendingSync) && onDiscardSync && (
												<Button
													variant="outlined"
													size="small"
													startIcon={<CancelIcon />}
													onClick={onDiscardSync}
													className={classes.syncButton}
													color="secondary"
												>
													Discard
												</Button>
											)}
										</div>
										{timesheet.lastError && (
											<Typography variant="caption" color="error" style={{ marginTop: 4, display: "block" }}>
												Error: {timesheet.lastError}
											</Typography>
										)}
									</div>
								)}
							</div>
						</div>

					{/* Options menu shown bottom-right only when expanded and not hidden by parent, or for active timesheets when admin */}
					{/* Show edit options for admins OR for staff when feature flag is enabled and they own the timesheet */}
					{/* Supervisors/admins also get approve/unapprove when timesheet approvals are enabled */}
					{((!isStaffUser(role) || canStaffEdit) && (!hideActions || (timesheet.isActive && onEndTimesheet))) || showApprovalActions ? (
							<Box mt={2} display="flex" justifyContent="flex-end" position="relative">
								<CrudOptions 
									position="bottom-right"
								onEdit={!hideActions && (!isStaffUser(role) || canStaffEdit) ? onEdit : undefined}
								onClone={!hideActions && (!isStaffUser(role) || canStaffClone) ? onClone : undefined}
								onDelete={!hideActions && !isStaffUser(role) ? () => onDelete(timesheet.id) : undefined}
								customOptions={[
									...(timesheet.isActive && onEndTimesheet ? [{
										label: "Stop Timesheet",
										icon: StopIcon,
										onClick: () => onEndTimesheet(timesheet.id)
									}] : []),
									...(showApprovalActions && !timesheet.isApproved && onApprove ? [{
										label: "Approve",
										icon: CheckCircleIcon,
										onClick: () => onApprove(timesheet.id)
									}] : []),
									...(showApprovalActions && timesheet.isApproved && onUnapprove ? [{
										label: "Unapprove",
										icon: RemoveCircleOutlineIcon,
										onClick: () => onUnapprove(timesheet.id)
									}] : []),
								]}
								/>
							</Box>
						) : null}

					{hasTimeEdits && timeFieldChanges.length > 0 && (
						<Box mt={1}>
							<ExpansionPanel
								className={classes.editsAccordion}
								elevation={0}
								expanded={editsExpanded}
								onChange={(_, isExpanded) => setEditsExpanded(isExpanded)}
							>
								<ExpansionPanelSummary
									expandIcon={<ExpandMoreIcon fontSize="small" />}
									className={classes.editsAccordionSummary}
									aria-controls={`timesheet-edits-${timesheet.id}`}
									id={`timesheet-edits-header-${timesheet.id}`}
								>
									<Typography className={classes.editsAccordionTitle}>
										Show Edits
									</Typography>
								</ExpansionPanelSummary>
								<ExpansionPanelDetails
									className={classes.editsAccordionDetails}
									id={`timesheet-edits-${timesheet.id}`}
								>
									<div className={classes.changeLog}>
										{timeFieldChanges.map((change) => (
											<div key={change.id || `${change.fieldName}-${change.changedUTC}`} className={classes.changeLogEntry}>
												<div>
													{fieldLabel(change.fieldName)}:{" "}
													{formatChangeValue(change.fieldName, change.oldValue)}
													{" → "}
													{formatChangeValue(change.fieldName, change.newValue)}
												</div>
												<div className={classes.changeLogMeta}>
													{formatDateTime(change.changedUTC)}
													{change.changedBy ? ` · ${change.changedBy}` : ""}
												</div>
											</div>
										))}
									</div>
								</ExpansionPanelDetails>
							</ExpansionPanel>
						</Box>
					)}
				</Collapse>
			</CardContent>
		</Card>
	);
};
